import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, Globe, KeyRound, Loader2, Server, ShieldCheck } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useCompany } from "@/hooks/useCompany";
import { TRIAL_READ_ONLY_MESSAGE, useTrialMode } from "@/hooks/useTrialMode";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const hostingTypes = [
  { value: "wordpress", label: "Managed site", description: "REST API or application password" },
  { value: "custom", label: "Custom", description: "HTTP endpoint or custom sync API" },
];

const syncModes = [
  { value: "manual", label: "Manual" },
  { value: "scheduled", label: "Scheduled" },
  { value: "webhook", label: "Webhook" },
];

function cleanDomain(value: string) {
  return value.trim().replace(/^https?:\/\//, "").replace(/\/$/, "").toLowerCase();
}

export default function ImportWebsiteWizard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentCompany, currentCompanyId, isPlatformOwner } = useCompany();
  const trialMode = useTrialMode();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [domain, setDomain] = useState("");
  const [hostingType, setHostingType] = useState("wordpress");
  const [apiUrl, setApiUrl] = useState("");
  const [apiUsername, setApiUsername] = useState("");
  const [apiToken, setApiToken] = useState("");
  const [syncMode, setSyncMode] = useState("manual");
  const [primaryLanguage, setPrimaryLanguage] = useState("en");

  const normalizedDomain = cleanDomain(domain);
  const canContinue = name.trim().length > 1 && normalizedDomain.includes(".");
  const canSave = canContinue && apiUrl.trim().length > 0 && apiToken.trim().length > 0;

  const checkWebsiteLimit = async () => {
    if (isPlatformOwner || !currentCompany?.subscription_plan_id || !currentCompanyId) return true;
    const [{ count }, { data: plan }] = await Promise.all([
      (supabase as any).from("websites").select("id", { count: "exact", head: true }).eq("company_id", currentCompanyId),
      (supabase as any).from("subscription_plans").select("website_limit").eq("id", currentCompany.subscription_plan_id).maybeSingle(),
    ]);
    const limit = plan?.website_limit ?? 1;
    if (limit >= 0 && (count ?? 0) >= limit) {
      toast.error(`Your company plan allows ${limit} website${limit === 1 ? "" : "s"}.`);
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (trialMode.isReadOnly) {
      toast.error(TRIAL_READ_ONLY_MESSAGE);
      return;
    }
    if (!user || !currentCompanyId) {
      toast.error("Select a company before importing a website");
      return;
    }
    if (!(await checkWebsiteLimit())) return;

    setSaving(true);
    try {
      const slug = normalizedDomain.replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
      const { error } = await (supabase as any).from("websites").insert({
        owner_id: user.id,
        company_id: currentCompanyId,
        name: name.trim(),
        slug: `${slug}-${Date.now()}`,
        domain: normalizedDomain,
        status: "active",
        primary_language: primaryLanguage,
        languages: [primaryLanguage],
        deployment_strategy: syncMode === "manual" ? "manual" : "semi_auto",
        sync_frequency: syncMode === "scheduled" ? "24h" : "manual",
        technical_config: {
          external_hosted: true,
          hosting_type: hostingType,
          api_url: apiUrl.trim(),
          api_username: apiUsername.trim() || null,
          sync_mode: syncMode,
          credentials_status: "provided",
          credential_hint: apiToken ? `${apiToken.slice(0, 4)}...${apiToken.slice(-4)}` : null,
        },
        automation_config: {
          enabled: syncMode !== "manual",
          syncMode,
        },
      });
      if (error) throw error;
      toast.success("External website imported");
      navigate("/websites");
    } catch (error: any) {
      toast.error(error?.message ?? "Could not import website");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-border bg-background/80 px-6 backdrop-blur-xl">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/websites")} className="rounded-md p-1.5 transition-colors hover:bg-accent">
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div>
            <h1 className="text-lg font-semibold text-foreground">Import external website</h1>
            <p className="text-xs text-muted-foreground">Connect a website hosted outside Delapresse</p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className={cn(step === 1 && "text-primary")}>Website</span>
          <ArrowRight className="h-3 w-3" />
          <span className={cn(step === 2 && "text-primary")}>Connection</span>
        </div>
      </header>

      <div className="mx-auto max-w-3xl space-y-6 px-6 py-8">
        {step === 1 && (
          <div className="space-y-5 rounded-lg border border-border bg-card p-5">
            <div className="grid gap-4 md:grid-cols-2">
              <label className="space-y-1.5">
                <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Website name</span>
                <input value={name} onChange={(event) => setName(event.target.value)} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" placeholder="My News Site" />
              </label>
              <label className="space-y-1.5">
                <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Domain</span>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <input value={domain} onChange={(event) => setDomain(event.target.value)} className="w-full rounded-md border border-input bg-background py-2 pl-9 pr-3 text-sm" placeholder="news.example.com" />
                </div>
              </label>
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              {hostingTypes.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  onClick={() => setHostingType(item.value)}
                  className={cn("rounded-lg border p-4 text-left transition-colors", hostingType === item.value ? "border-primary bg-primary/5" : "border-border hover:bg-accent/40")}
                >
                  <Server className="mb-2 h-5 w-5 text-primary" />
                  <p className="text-sm font-medium text-foreground">{item.label}</p>
                  <p className="text-xs text-muted-foreground">{item.description}</p>
                </button>
              ))}
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <label className="space-y-1.5">
                <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Primary language</span>
                <select value={primaryLanguage} onChange={(event) => setPrimaryLanguage(event.target.value)} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                  <option value="en">English</option>
                  <option value="fr">Francais</option>
                  <option value="ar">Arabic</option>
                  <option value="es">Spanish</option>
                </select>
              </label>
              <label className="space-y-1.5">
                <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Sync mode</span>
                <select value={syncMode} onChange={(event) => setSyncMode(event.target.value)} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                  {syncModes.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                </select>
              </label>
            </div>

            <div className="flex justify-end">
              <button disabled={!canContinue} onClick={() => setStep(2)} className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50">
                Continue <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-5 rounded-lg border border-border bg-card p-5">
            <div className="rounded-lg border border-border bg-background p-4">
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-primary" />
                <p className="text-sm font-medium text-foreground">{hostingType === "wordpress" ? "Site connection" : "Custom API connection"}</p>
              </div>
            </div>

            <label className="space-y-1.5">
              <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">API endpoint</span>
              <input value={apiUrl} onChange={(event) => setApiUrl(event.target.value)} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" placeholder={hostingType === "wordpress" ? `https://${normalizedDomain}/wp-json/wp/v2` : `https://${normalizedDomain}/api/sync`} />
            </label>

            <div className="grid gap-4 md:grid-cols-2">
              <label className="space-y-1.5">
                <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Username or key id</span>
                <input value={apiUsername} onChange={(event) => setApiUsername(event.target.value)} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" placeholder="editor@example.com" />
              </label>
              <label className="space-y-1.5">
                <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">API token</span>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <input type="password" value={apiToken} onChange={(event) => setApiToken(event.target.value)} className="w-full rounded-md border border-input bg-background py-2 pl-9 pr-3 text-sm" placeholder="Application password or API token" />
                </div>
              </label>
            </div>

            <div className="flex justify-between">
              <button onClick={() => setStep(1)} className="rounded-md border border-border px-4 py-2 text-sm hover:bg-accent">Back</button>
              <button disabled={!canSave || saving || trialMode.isReadOnly} onClick={handleSave} title={trialMode.isReadOnly ? TRIAL_READ_ONLY_MESSAGE : undefined} className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                Import website
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
